import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { Button } from "../components/Button";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { AddToCalendarButton } from "../components/AddToCalendarButton";
import { SavingsSummary } from "../components/SavingsSummary";
import { RoofLocationSection } from "../components/RoofLocationSection";
import { getLead, getSalesCall, getSelfIPALinks } from '../services/enpalForYouService';
import { CalendarEvent, Lead, SelfIPALinks } from '../types';

export const DashboardPage: React.FC = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [salesCall, setSalesCall] = useState<CalendarEvent | null>(null);
  const [salesCallLoading, setSalesCallLoading] = useState<boolean>(true);
  const [lead, setLead] = useState<Lead | null>(null);
  const [leadLoading, setLeadLoading] = useState<boolean>(true);
  const [selfIPALinks, setSelfIPALinks] = useState<SelfIPALinks | null>(null);
  const [selfIPALinksLoading, setSelfIPALinksLoading] = useState<boolean>(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [leadError, setLeadError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSalesCall = async (): Promise<void> => {
      setSalesCallLoading(true);
      try {
        const event = await getSalesCall();
        setSalesCall(event);
      } catch {
        setSalesCall(null);
      } finally {
        setSalesCallLoading(false);
      }
    };

    fetchSalesCall();
  }, []);

  useEffect(() => {
    const fetchSelfIPALinks = async (): Promise<void> => {
      if (!user) {
        setSelfIPALinks(null);
        setSelfIPALinksLoading(false);
        return;
      }

      setSelfIPALinksLoading(true);
      try {
        const links = await getSelfIPALinks();
        setSelfIPALinks(links);
      } catch {
        setSelfIPALinks(null);
      } finally {
        setSelfIPALinksLoading(false);
      }
    };

    fetchSelfIPALinks();
  }, [user]);

  useEffect(() => {
    const fetchLead = async (): Promise<void> => {
      setLeadLoading(true);
      setLeadError(null);
      try {
        const leadData = await getLead();
        setLead(leadData);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to load lead data';
        setLeadError(errorMessage);
      } finally {
        setLeadLoading(false);
      }
    };

    fetchLead();
  }, [user?.email]);

  const handleLogout = async (): Promise<void> => {
    await logout();
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="page-shell mobile-safe-padding mobile-safe-top flex min-h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!user) {
    navigate("/login");
    return null;
  }

  const hasSelfIPALinks = !selfIPALinksLoading && Boolean(selfIPALinks && (selfIPALinks.pv || selfIPALinks.hp));

  return (
    <div className="page-shell dashboard-shell mobile-safe-padding mobile-safe-top">
      <div className="dashboard-top-layer">
        <div className="page-header mb-0">
          <img src="/enpal-logo.png" alt="Enpal" className="h-10 w-auto" />
          <Button variant="secondary" onClick={handleLogout}>
            Esci
          </Button>
        </div>
      </div>

      {(salesCallLoading || salesCall) && (
        <section className="dashboard-appointment-spotlight mt-5" data-testid="sales-call-card">
          <div className="mb-5">
            <p className="dashboard-kicker">Prossimo step</p>
            <h2 className="mt-2 text-2xl font-bold text-enpal-downriver sm:text-3xl">Il tuo appuntamento con Enpal</h2>
          </div>

          {salesCallLoading && (
            <div className="dashboard-glass-panel flex flex-col items-center gap-4 py-8">
              <LoadingSpinner />
              <p className="text-base text-enpal-stone-grey">Caricamento appuntamento...</p>
            </div>
          )}

          {!salesCallLoading && salesCall && (
            <div className="dashboard-glass-panel">
              {salesCall.description && (
                <p className="mb-5 text-sm leading-6 text-enpal-stone-grey sm:text-base">{salesCall.description}</p>
              )}

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="dashboard-metadata-pill">
                  <span className="font-semibold text-enpal-downriver">Data:</span>
                  <span className="font-semibold text-enpal-downriver">
                    {salesCall.startDate.toLocaleDateString('it-IT', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                </div>

                <div className="dashboard-metadata-pill">
                  <span className="font-semibold text-enpal-downriver">Orario:</span>
                  <span className="font-semibold text-enpal-downriver">
                    {salesCall.startDate.toLocaleTimeString('it-IT', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap justify-center gap-3">
                {salesCall.url && (
                  <a
                    href={salesCall.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary min-w-[240px] justify-center"
                  >
                    Accedi alla videochiamata
                  </a>
                )}

                <AddToCalendarButton
                  event={salesCall}
                  filename="chiamata-enpal.ics"
                  variant={salesCall.url ? 'secondary' : 'primary'}
                  className="min-w-[240px] justify-center"
                >
                  Aggiungi al Calendario
                </AddToCalendarButton>
              </div>
            </div>
          )}
        </section>
      )}

      <div className="mt-7 space-y-6">
        <RoofLocationSection
          lead={lead}
          leadLoading={leadLoading}
          onRoofLocationSaved={async () => {
            try {
              const data = await getLead();
              setLead(data);
            } catch {
              // Non-blocking: local save still succeeded; refetch is best-effort.
            }
          }}
        />

        {hasSelfIPALinks && (
          <section className="dashboard-side-card" data-testid="self-ipa-card">
            <p className="dashboard-kicker">Azioni rapide</p>
            <h2 className="mt-2 text-3xl font-bold leading-tight text-enpal-downriver">
              Personalizza il tuo preventivo
            </h2>
            <p className="mt-3 text-base leading-7 text-enpal-stone-grey">
              Aggiungi le foto della tua abitazione per ottenere un preventivo più accurato dal tuo consulente.
            </p>
            <div className="mt-5 grid gap-3">
              {selfIPALinks?.pv && (
                <a
                  href={selfIPALinks.pv}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary justify-self-center"
                >
                  Carica le foto qui per il Fotovoltaico
                </a>
              )}
              {selfIPALinks?.hp && (
                <a
                  href={selfIPALinks.hp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary justify-self-center"
                >
                  Carica le foto qui per la Pompa di Calore
                </a>
              )}
            </div>
          </section>
        )}

        <section className="dashboard-main-card" data-testid="cer-enpal-card">
          <div className="mb-5 flex items-center justify-between gap-4 border-b border-slate-200 pb-4">
            <div>
              <p className="dashboard-kicker">Comunità energetica</p>
              <h2 className="mt-2 text-2xl font-bold text-enpal-downriver sm:text-3xl">Scopri la CER Enpal</h2>
            </div>
            <span className="dashboard-chip">Gratis e senza vincoli</span>
          </div>

          <p className="text-base leading-7 text-enpal-stone-grey">
            Vuoi capire meglio cos&apos;è la CER Enpal, come funziona e quali vantaggi può offrirti? Scopri tutti i
            dettagli nella pagina dedicata.
          </p>

          <div className="mt-6 flex justify-center">
            <a
              href="https://cerenpal.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary min-w-[240px] justify-center"
            >
              Scopri di più
            </a>
          </div>
        </section>

        <section className="dashboard-main-card" data-testid="savings-summary-card">
          <div className="mb-5 flex items-center justify-between gap-4 border-b border-slate-200 pb-4">
            <h2 className="text-2xl font-bold text-enpal-downriver sm:text-3xl">Riepilogo Risparmio</h2>
            <span className="dashboard-chip">Bonus e sconti</span>
          </div>

          {!leadLoading && lead && lead.bonusSummaryData && (
            <SavingsSummary productType={lead.productType} bonusSummaryData={lead.bonusSummaryData} />
          )}

          {!leadLoading && lead && !lead.bonusSummaryData && (
            <div className="enpal-subtle-card text-center">
              <p className="text-base text-enpal-stone-grey">Nessun riepilogo risparmio disponibile.</p>
            </div>
          )}

          {!leadLoading && !lead && (
            <div className="enpal-subtle-card text-center">
              <p className="text-base text-enpal-stone-grey">
                Purtroppo non abbiamo trovato nessuna richiesta associata al tuo account.
              </p>
              <p className="mt-4 text-base text-enpal-stone-grey">
                Per richiedere un preventivo e accedere al tuo riepilogo risparmio, compila il modulo sul nostro sito:
              </p>
              <a href="https://enpal.it/" target="_blank" rel="noopener noreferrer" className="btn-primary mt-4">
                Vai al modulo di richiesta
              </a>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};
